import type { SelectOption } from '../../React/Common/Components/Selects/Types'
import { DeviceTypesLanguage } from '../../../i18n'
import CLanguageHandler from '../Languages/LanguageHandler'
import { isVarNotEmpty } from '../utils/GlobalFunctions'
import { DEVICE_TYPES_MODAL_ADD_ORDER, DeviceType, DeviceTypeApiString } from './DeviceTypes'
import { TelSysTypes } from './TelephonyServerTypes'

/**
 * <p>Maps each {@link DeviceType} to its {@link DeviceTypesLanguage} bundle key for {@link CLanguageHandler.get}.</p>
 */
const DEVICE_TYPE_TO_LANGUAGE_KEY: Readonly<Record<DeviceType, string>> = Object.freeze({
    [DeviceType.Unknown]: DeviceTypesLanguage.DeviceTypesLanguage_Unknown,
    [DeviceType.Trunk]: DeviceTypesLanguage.DeviceTypesLanguage_Trunk,
    [DeviceType.TrunkGateway]: DeviceTypesLanguage.DeviceTypesLanguage_TrunkGateway,
    [DeviceType.ChatService]: DeviceTypesLanguage.DeviceTypesLanguage_ChatService,
    [DeviceType.EmailServer]: DeviceTypesLanguage.DeviceTypesLanguage_EmailServer,
    [DeviceType.ExtensionOrEndpoint]: DeviceTypesLanguage.DeviceTypesLanguage_ExtensionOrEndpoint,
    [DeviceType.ExtensionOrEndpointTrunking]: DeviceTypesLanguage.DeviceTypesLanguage_ExtensionOrEndpointTrunking,
    [DeviceType.ExtensionOrEndpointHospitality]: DeviceTypesLanguage.DeviceTypesLanguage_ExtensionOrEndpointHospitality,
    [DeviceType.GroupSuper]: DeviceTypesLanguage.DeviceTypesLanguage_GroupSuper,
    [DeviceType.GroupCollective]: DeviceTypesLanguage.DeviceTypesLanguage_GroupCollective,
    [DeviceType.GroupLinear]: DeviceTypesLanguage.DeviceTypesLanguage_GroupLinear,
    [DeviceType.GroupCyclic]: DeviceTypesLanguage.DeviceTypesLanguage_GroupCyclic,
    [DeviceType.GroupAcd]: DeviceTypesLanguage.DeviceTypesLanguage_GroupAcd,
    [DeviceType.GroupTrunk]: DeviceTypesLanguage.DeviceTypesLanguage_GroupTrunk,
    [DeviceType.GroupVoicemail]: DeviceTypesLanguage.DeviceTypesLanguage_GroupVoicemail,
    [DeviceType.GroupPage]: DeviceTypesLanguage.DeviceTypesLanguage_GroupPage,
    [DeviceType.GroupDepartment]: DeviceTypesLanguage.DeviceTypesLanguage_GroupDepartment,
    [DeviceType.GroupSecurityGroup]: DeviceTypesLanguage.DeviceTypesLanguage_GroupSecurityGroup,
    [DeviceType.Park]: DeviceTypesLanguage.DeviceTypesLanguage_Park,
    [DeviceType.VoicemailApplication]: DeviceTypesLanguage.DeviceTypesLanguage_VoicemailApplication,
    [DeviceType.VoicemailAnnouncementHuntGroup]: DeviceTypesLanguage.DeviceTypesLanguage_VoicemailAnnouncementHuntGroup,
    [DeviceType.VoicemailAnnouncementGeneral]: DeviceTypesLanguage.DeviceTypesLanguage_VoicemailAnnouncementGeneral,
    [DeviceType.AutoAttendant]: DeviceTypesLanguage.DeviceTypesLanguage_AutoAttendant,
    [DeviceType.MusicOnHold]: DeviceTypesLanguage.DeviceTypesLanguage_MusicOnHold,
    [DeviceType.Conference]: DeviceTypesLanguage.DeviceTypesLanguage_Conference,
    [DeviceType.PilotNumber]: DeviceTypesLanguage.DeviceTypesLanguage_PilotNumber,
    [DeviceType.DnisEntry]: DeviceTypesLanguage.DeviceTypesLanguage_DnisEntry
})

/** Maps each numeric {@link DeviceType} to the backend {@code DeviceType} string. */
const DEVICE_TYPE_TO_API_VALUE: Readonly<Record<DeviceType, DeviceTypeApiString>> = Object.freeze({
    [DeviceType.Unknown]: DeviceTypeApiString.Unknown,
    [DeviceType.Trunk]: DeviceTypeApiString.Trunk,
    [DeviceType.TrunkGateway]: DeviceTypeApiString.TrunkGateway,
    [DeviceType.ChatService]: DeviceTypeApiString.ChatService,
    [DeviceType.EmailServer]: DeviceTypeApiString.EmailServer,
    [DeviceType.ExtensionOrEndpoint]: DeviceTypeApiString.ExtensionOrEndpoint,
    [DeviceType.ExtensionOrEndpointTrunking]: DeviceTypeApiString.ExtensionOrEndpointTrunking,
    [DeviceType.ExtensionOrEndpointHospitality]: DeviceTypeApiString.ExtensionOrEndpointHospitality,
    [DeviceType.GroupSuper]: DeviceTypeApiString.GroupSuper,
    [DeviceType.GroupCollective]: DeviceTypeApiString.GroupCollective,
    [DeviceType.GroupLinear]: DeviceTypeApiString.GroupLinear,
    [DeviceType.GroupCyclic]: DeviceTypeApiString.GroupCyclic,
    [DeviceType.GroupAcd]: DeviceTypeApiString.GroupAcd,
    [DeviceType.GroupTrunk]: DeviceTypeApiString.GroupTrunk,
    [DeviceType.GroupVoicemail]: DeviceTypeApiString.GroupVoicemail,
    [DeviceType.GroupPage]: DeviceTypeApiString.GroupPage,
    [DeviceType.GroupDepartment]: DeviceTypeApiString.GroupDepartment,
    [DeviceType.GroupSecurityGroup]: DeviceTypeApiString.GroupSecurityGroup,
    [DeviceType.Park]: DeviceTypeApiString.Park,
    [DeviceType.VoicemailApplication]: DeviceTypeApiString.VoicemailApplication,
    [DeviceType.VoicemailAnnouncementHuntGroup]: DeviceTypeApiString.VoicemailAnnouncementHuntGroup,
    [DeviceType.VoicemailAnnouncementGeneral]: DeviceTypeApiString.VoicemailAnnouncementGeneral,
    [DeviceType.AutoAttendant]: DeviceTypeApiString.AutoAttendant,
    [DeviceType.MusicOnHold]: DeviceTypeApiString.MusicOnHold,
    [DeviceType.Conference]: DeviceTypeApiString.Conference,
    [DeviceType.PilotNumber]: DeviceTypeApiString.PilotNumber,
    [DeviceType.DnisEntry]: DeviceTypeApiString.DnisEntry
})

type DeviceTypeLabelOverride = Readonly<{
    deviceType: DeviceType
    telSysType: TelSysTypes
    languageKey: string
}>

type DeviceTypeApiValueOverride = Readonly<{
    deviceType: DeviceType
    telSysType: TelSysTypes
    apiValue: DeviceTypeApiString
}>

const DEVICE_TYPE_LABEL_OVERRIDES: readonly DeviceTypeLabelOverride[] = Object.freeze([
    {
        deviceType: DeviceType.GroupSecurityGroup,
        telSysType: TelSysTypes.BROADSOFT_BROADWORKS,
        languageKey: DeviceTypesLanguage.DeviceTypesLanguage_GroupSecurityGroupBroadworks
    },
    {
        deviceType: DeviceType.GroupAcd,
        telSysType: TelSysTypes.MS_TEAMS,
        languageKey: DeviceTypesLanguage.DeviceTypesLanguage_GroupAcdMsTeams
    }
])

const DEVICE_TYPE_API_VALUE_OVERRIDES: readonly DeviceTypeApiValueOverride[] = Object.freeze([
    {
        deviceType: DeviceType.GroupAcd,
        telSysType: TelSysTypes.MS_TEAMS,
        apiValue: DeviceTypeApiString.CallQueue
    }
])

/**
 * <p>Returns the language key used for the given {@link DeviceType}.</p>
 *
 * @param deviceType - Numeric device type enum member.
 * @returns Bundle key accepted by {@link CLanguageHandler.get}.
 */
export const mapDeviceTypeToLanguageKey = (deviceType: DeviceType): string => DEVICE_TYPE_TO_LANGUAGE_KEY[deviceType]

/**
 * <p>Returns the backend {@code DeviceType} string for a numeric device type.</p>
 *
 * @param deviceType - Numeric device type enum member.
 * @param telSysType - Optional telephony system type for backend string overrides.
 * @returns Canonical backend request/response string.
 */
export const getDeviceTypeApiValue = (deviceType: DeviceType, telSysType?: TelSysTypes): DeviceTypeApiString => {
    const override: Definable<DeviceTypeApiValueOverride> = DEVICE_TYPE_API_VALUE_OVERRIDES.find(
        (apiValueOverride: DeviceTypeApiValueOverride): boolean =>
            apiValueOverride.deviceType === deviceType && apiValueOverride.telSysType === telSysType
    )

    return override?.apiValue ?? DEVICE_TYPE_TO_API_VALUE[deviceType]
}

/**
 * <p>Parses a backend {@code DeviceType} string into its numeric {@link DeviceType} enum member.</p>
 *
 * @param deviceTypeValue - Backend request/response string.
 * @param fallbackDeviceType - Fallback enum member when the value is unknown.
 * @returns Numeric device type enum member.
 */
export const parseDeviceTypeApiValue = (
    deviceTypeValue: string,
    fallbackDeviceType: DeviceType = DeviceType.ExtensionOrEndpoint
): DeviceType => {
    const trimmedDeviceTypeValue: string = deviceTypeValue.trim()

    const parsedNumericDeviceType: number = Number.parseInt(trimmedDeviceTypeValue, 10)

    if (!Number.isNaN(parsedNumericDeviceType)) {
        return parsedNumericDeviceType
    }

    const matchedOverride: Definable<DeviceTypeApiValueOverride> = DEVICE_TYPE_API_VALUE_OVERRIDES.find(
        (apiValueOverride): boolean => apiValueOverride.apiValue === trimmedDeviceTypeValue
    )

    if (isVarNotEmpty(matchedOverride)) {
        return matchedOverride.deviceType
    }

    const matchedEntry: Definable<[string, DeviceTypeApiString]> = Object.entries(DEVICE_TYPE_TO_API_VALUE).find(
        ([, apiValue]): boolean => apiValue === trimmedDeviceTypeValue
    )

    if (isVarNotEmpty(matchedEntry)) {
        return Number(matchedEntry[0])
    }

    return fallbackDeviceType
}

/**
 * Resolves the localised display label for a device type.
 *
 * @param deviceType - Device type enum member.
 * @param language - Active language handler; caller must load {@link DeviceTypesLanguage} before calling.
 * @param telSysType - Optional telephony system type for device-name overrides.
 * @returns User-visible device type name.
 */
export const getDeviceTypeLabel = (
    deviceType: DeviceType,
    language: CLanguageHandler,
    telSysType?: TelSysTypes
): string => {
    const override: Definable<DeviceTypeLabelOverride> = DEVICE_TYPE_LABEL_OVERRIDES.find(
        (labelOverride): boolean => labelOverride.deviceType === deviceType && labelOverride.telSysType === telSysType
    )

    if (isVarNotEmpty(override)) {
        return language.get(override.languageKey)
    }

    return language.get(mapDeviceTypeToLanguageKey(deviceType))
}

/**
 * <p>Builds select options for the device modal add flow, ordered like {@code CTelephonyModelConst.pDEVICE_TYPES}.</p>
 *
 * @param language - Active language handler with {@link DeviceTypesLanguage} loaded.
 * @param telSysType - Optional telephony system type for device-name overrides.
 * @returns Options whose {@code value} is the string form of the device type id.
 */
export const buildDeviceTypeModalSelectOptions = (
    language: CLanguageHandler,
    telSysType?: TelSysTypes
): SelectOption<string>[] =>
    DEVICE_TYPES_MODAL_ADD_ORDER.map(
        (deviceType: DeviceType): SelectOption<string> => ({
            value: getDeviceTypeApiValue(deviceType, telSysType),
            label: getDeviceTypeLabel(deviceType, language, telSysType)
        })
    )
